import {
  OktonCollapseCard as CollapseCard,
  OktonEmptyState,
  OktonSelect,
} from '@okton/platform-ui';
import React, { useEffect, useState } from 'react';
import {
  Box,
  Button,
  Checkbox,
  CheckboxGroup,
  Flex,
  Heading,
  IconButton,
  SimpleGrid,
  Stack,
  Text,
  VStack,
  useColorModeValue,
  useToast,
} from '@chakra-ui/react';
import { AxiosError } from 'axios';
import { FiArrowLeft, FiSave } from 'react-icons/fi';
import { useHistory, useParams } from 'react-router-dom';
import UsuarioDetalhesSkeleton from './UsuarioDetalhesSkeleton';

import AnalyticsPage from '../../layout/IntegraAppPage';
import backend from '../../services/backend';

type Filial = {
  id: number;
  nome: string;
  cnpj?: string | null;
};

type Usuario = {
  id: number;
  nome: string;
  email: string;
  filiais: number[];
};

type Permissao = {
  id: number;
  nome: string;
  chave: string;
  descricao?: string | null;
};

type PermissoesPorFilial = Record<string, number[]>;

function obterEmpresaIdUsuarioLogado(): number | null {
  const usuarioSalvo =
    localStorage.getItem('@DS850:user') || localStorage.getItem('user');

  if (!usuarioSalvo) return null;

  try {
    const usuario = JSON.parse(usuarioSalvo);
    const empresaId = Number(usuario?.empresa_id || usuario?.empresa);

    return empresaId > 0 ? empresaId : null;
  } catch {
    return null;
  }
}

const Info = ({ label, value }: { label: string; value: React.ReactNode }) => {
  const labelColor = useColorModeValue('gray.600', 'gray.400');
  const valueColor = useColorModeValue('gray.800', 'whiteAlpha.900');

  return (
    <Box minW={0}>
      <Text fontSize="xs" color={labelColor} mb={1}>
        {label}
      </Text>
      <Text fontSize="sm" fontWeight="600" color={valueColor}>
        {value || '-'}
      </Text>
    </Box>
  );
};

const UsuarioDetalhes: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const history = useHistory();
  const toast = useToast();

  const empresaId = obterEmpresaIdUsuarioLogado();

  const pageBg = useColorModeValue('#EEF5F9', 'gray.900');
  const cardBg = useColorModeValue('white', 'gray.800');
  const pageText = useColorModeValue('gray.800', 'white');
  const mutedColor = useColorModeValue('gray.600', 'gray.400');
  const borderColor = useColorModeValue('gray.200', 'gray.700');
  const primaryButtonScheme = useColorModeValue('blue', 'cyan');

  const [usuario, setUsuario] = useState<Usuario | null>(null);
  const [filiais, setFiliais] = useState<Filial[]>([]);
  const [permissoes, setPermissoes] = useState<Permissao[]>([]);
  const [filiaisSelecionadas, setFiliaisSelecionadas] = useState<string[]>([]);
  const [filialPermissaoSelecionada, setFilialPermissaoSelecionada] =
    useState('');
  const [permissoesSelecionadas, setPermissoesSelecionadas] = useState<
    string[]
  >([]);
  const [permissoesPorFilial, setPermissoesPorFilial] =
    useState<PermissoesPorFilial>({});
  const [carregando, setCarregando] = useState(true);
  const [salvandoFiliais, setSalvandoFiliais] = useState(false);
  const [salvandoPermissoes, setSalvandoPermissoes] = useState(false);

  useEffect(() => {
    async function carregarDados() {
      try {
        setCarregando(true);

        const [
          usuarioResponse,
          filiaisResponse,
          permissoesResponse,
          permissoesUsuarioResponse,
        ] = await Promise.all([
          backend.get(`/admin-empresa/usuarios/${id}`),
          backend.get('/admin-empresa/filiais'),
          backend.get('/admin-empresa/permissoes'),
          backend.get(`/admin-empresa/usuarios/${id}/permissoes`),
        ]);

        const usuarioApi = usuarioResponse.data?.data;
        const filiaisUsuario = (usuarioApi?.filiais || []).map(String);

        setUsuario(usuarioApi);
        setFiliais(filiaisResponse.data?.data || []);
        setPermissoes(permissoesResponse.data?.data || []);
        setFiliaisSelecionadas(filiaisUsuario);

        const normalizado: PermissoesPorFilial = {};

        Object.entries(permissoesUsuarioResponse.data?.data || {}).forEach(
          ([filialId, lista]) => {
            normalizado[filialId] = (lista as Permissao[]).map(permissao =>
              Number(permissao.id),
            );
          },
        );

        setPermissoesPorFilial(normalizado);

        const primeiraFilial = filiaisUsuario[0] || '';
        setFilialPermissaoSelecionada(primeiraFilial);
        setPermissoesSelecionadas(
          (normalizado[primeiraFilial] || []).map(String),
        );
      } catch (error) {
        const err = error as AxiosError<{ message?: string }>;

        toast({
          title:
            err.response?.data?.message || 'Erro ao carregar dados do usuário.',
          status: 'error',
          isClosable: true,
          position: 'bottom-right',
        });
      } finally {
        setCarregando(false);
      }
    }

    carregarDados();
  }, [id, toast]);

  const trocarFilialPermissao = (filialId: string) => {
    setFilialPermissaoSelecionada(filialId);
    setPermissoesSelecionadas(
      (permissoesPorFilial[filialId] || []).map(String),
    );
  };

  const salvarFiliais = async () => {
    if (!usuario || !empresaId) return;

    try {
      setSalvandoFiliais(true);

      await backend.put(`/usuarios/${usuario.id}/filiais`, {
        empresa_id: empresaId,
        filiais: filiaisSelecionadas.map(Number),
      });

      const filiaisAtualizadas = filiaisSelecionadas.map(Number);

      setUsuario({
        ...usuario,
        filiais: filiaisAtualizadas,
      });

      if (
        filialPermissaoSelecionada &&
        !filiaisSelecionadas.includes(filialPermissaoSelecionada)
      ) {
        const primeiraFilial = filiaisSelecionadas[0] || '';
        setFilialPermissaoSelecionada(primeiraFilial);
        setPermissoesSelecionadas(
          (permissoesPorFilial[primeiraFilial] || []).map(String),
        );
      }

      toast({
        title: 'Filiais atualizadas',
        status: 'success',
        isClosable: true,
        position: 'bottom-right',
      });
    } catch (error) {
      const err = error as AxiosError<{ message?: string }>;

      toast({
        title: err.response?.data?.message || 'Erro ao atualizar filiais.',
        status: 'error',
        isClosable: true,
        position: 'bottom-right',
      });
    } finally {
      setSalvandoFiliais(false);
    }
  };

  const salvarPermissoes = async () => {
    if (!usuario || !filialPermissaoSelecionada) return;

    try {
      setSalvandoPermissoes(true);

      await backend.put(`/admin-empresa/usuarios/${usuario.id}/permissoes`, {
        filial_id: Number(filialPermissaoSelecionada),
        permissoes: permissoesSelecionadas.map(Number),
      });

      setPermissoesPorFilial(prev => ({
        ...prev,
        [filialPermissaoSelecionada]: permissoesSelecionadas.map(Number),
      }));

      toast({
        title: 'Permissões atualizadas',
        status: 'success',
        isClosable: true,
        position: 'bottom-right',
      });
    } catch (error) {
      const err = error as AxiosError<{ message?: string }>;

      toast({
        title: err.response?.data?.message || 'Erro ao atualizar permissões.',
        status: 'error',
        isClosable: true,
        position: 'bottom-right',
      });
    } finally {
      setSalvandoPermissoes(false);
    }
  };

  if (carregando) {
    return <UsuarioDetalhesSkeleton />;
  }

  if (!usuario) {
    return (
      <AnalyticsPage>
        <OktonEmptyState
          title="Usuário não encontrado"
          description="O usuário solicitado não está disponível."
          action={
            <Button
              leftIcon={<FiArrowLeft />}
              onClick={() => history.push('/admin-empresa')}
            >
              Voltar
            </Button>
          }
        />
      </AnalyticsPage>
    );
  }

  return (
    <AnalyticsPage>
      <Box bg={pageBg} minH="100vh" p={6} color={pageText}>
        <Flex align="center" mb={6} gap={4}>
          <IconButton
            aria-label="Voltar"
            icon={<FiArrowLeft />}
            variant="ghost"
            borderRadius="full"
            onClick={() => history.goBack()}
          />

          <VStack align="start" spacing={0}>
            <Heading size="md">{usuario.nome}</Heading>
            <Text fontSize="xs" color={mutedColor}>
              {usuario.email}
            </Text>
          </VStack>
        </Flex>

        <Stack spacing={6}>
          <CollapseCard title="Dados do usuário" defaultOpen>
            <SimpleGrid columns={{ base: 1, md: 2, xl: 4 }} spacing={5}>
              <Info label="Nome" value={usuario.nome} />
              <Info label="Email" value={usuario.email} />
              <Info label="ID do usuário" value={usuario.id} />
              <Info label="Filiais vinculadas" value={usuario.filiais.length} />
            </SimpleGrid>
          </CollapseCard>

          <CollapseCard title="Filiais permitidas" defaultOpen>
            <CheckboxGroup
              value={filiaisSelecionadas}
              onChange={values => setFiliaisSelecionadas(values.map(String))}
            >
              <Stack
                spacing={0}
                borderWidth="1px"
                borderColor={borderColor}
                borderRadius="lg"
                overflow="hidden"
              >
                {filiais.map(filial => (
                  <Flex
                    key={filial.id}
                    justify="space-between"
                    align="center"
                    px={4}
                    py={3}
                    borderBottomWidth="1px"
                    borderColor={borderColor}
                    bg={cardBg}
                  >
                    <Box>
                      <Text fontWeight="medium">{filial.nome}</Text>
                      <Text fontSize="xs" color={mutedColor}>
                        ID {filial.id}
                      </Text>
                    </Box>

                    <Checkbox value={String(filial.id)} />
                  </Flex>
                ))}
              </Stack>
            </CheckboxGroup>

            <Flex justify="flex-end" mt={4}>
              <Button
                colorScheme={primaryButtonScheme}
                leftIcon={<FiSave />}
                isLoading={salvandoFiliais}
                onClick={salvarFiliais}
              >
                Salvar filiais
              </Button>
            </Flex>
          </CollapseCard>

          <CollapseCard title="Permissões" defaultOpen>
            <Stack spacing={4}>
              <OktonSelect
                name="filialPermissao"
                label="Filial"
                value={filialPermissaoSelecionada}
                onChange={event => trocarFilialPermissao(event.target.value)}
                placeholder={
                  filiaisSelecionadas.length
                    ? undefined
                    : 'Vincule uma filial primeiro'
                }
                isDisabled={filiaisSelecionadas.length === 0}
              >
                {filiaisSelecionadas.map(filialId => {
                  const filial = filiais.find(
                    item => item.id === Number(filialId),
                  );

                  return (
                    <option key={filialId} value={filialId}>
                      {filial?.nome || `Filial ${filialId}`}
                    </option>
                  );
                })}
              </OktonSelect>

              <CheckboxGroup
                value={permissoesSelecionadas}
                onChange={values =>
                  setPermissoesSelecionadas(values.map(String))
                }
              >
                <Stack
                  spacing={0}
                  borderWidth="1px"
                  borderColor={borderColor}
                  borderRadius="lg"
                  overflow="hidden"
                >
                  {permissoes.map(permissao => (
                    <Flex
                      key={permissao.id}
                      justify="space-between"
                      align="center"
                      px={4}
                      py={3}
                      borderBottomWidth="1px"
                      borderColor={borderColor}
                      bg={cardBg}
                    >
                      <Box>
                        <Text fontWeight="medium">{permissao.nome}</Text>
                        <Text fontSize="xs" color={mutedColor}>
                          {permissao.chave}
                        </Text>
                      </Box>

                      <Checkbox
                        value={String(permissao.id)}
                        isDisabled={!filialPermissaoSelecionada}
                      />
                    </Flex>
                  ))}
                </Stack>
              </CheckboxGroup>

              <Flex justify="flex-end">
                <Button
                  colorScheme={primaryButtonScheme}
                  leftIcon={<FiSave />}
                  isLoading={salvandoPermissoes}
                  onClick={salvarPermissoes}
                  isDisabled={!filialPermissaoSelecionada}
                >
                  Salvar permissões
                </Button>
              </Flex>
            </Stack>
          </CollapseCard>
        </Stack>
      </Box>
    </AnalyticsPage>
  );
};

export default UsuarioDetalhes;
